import type { LegacyUserId } from "../identity";
// Added to `panels` by S3, which backfills every existing row to 'published'.
export type PanelStatus = 'draft' | 'published' | 'hidden' | 'archived';

// labiq's WheelVisibility reason, served only on a staff include_hidden read; null = viewers see the node.
export type HiddenReason = 'hidden' | 'trait_hidden' | 'gene_hidden' | 'low_quality' | 'no_visible_snps';

export interface Panel {
    id_panels: number;
    name_panel: string;
    desc_long: string | null;
    desc_short: string | null;
    red_tag: string | null;
    green_tag: string | null;
    modality: string | null;
    panel_sex: string | null;
    // Optional until S3 lands; absent means 'published', which is what its backfill produces.
    status?: PanelStatus;
    owner_user_id?: LegacyUserId | null;
    origin_panel_id?: number | null;
    // Virtual properties
    snp_count?: number;
    trait_count?: number;
}

export interface PanelSummary {
    id: number;
    name: string;
    description: string;
    modality: string | null;
    panel_sex: string | null;
    trait_count: number;
    snp_count: number;
    gene_count: number;
    quality_distribution: Record<string, number>;
    subcategory_distribution: Record<string, number>;
}

export interface PanelUser {
    idauth_samples: number;
    auth_login: string;
    sampleID: string;
    sample_activation: number;
}

// One gene-ring entry as GET /panels/{id}/traits serves it (A4). `id: null` is the server-side
// fallback for a (trait, gene) pair whose wheel_genes row does not exist yet (backfill not run).
export interface WheelGeneRef {
    id: number | null;
    symbol: string;
    sort_order: number | null;
    registry_ref: string | null;
    is_hidden?: boolean;
    hidden_reason?: HiddenReason | null;
}

export interface PanelTrait {
    idtraits: number;
    trait_panel: string;
    trait_name: string;
    trait_desc: string | null;
    trait_searcher: string | null;
    lifetime_risk: string | null;
    trait_treatment: string | null;
    snp_count?: number;
    // Absent on payloads from a labiq that predates A4; the ring then derives name-keyed nodes.
    genes?: WheelGeneRef[];
    is_hidden?: boolean;
    hidden_reason?: HiddenReason | null;
}

export const defaultPanel: Panel = {
    id_panels: 0,
    name_panel: '',
    desc_long: null,
    desc_short: null,
    red_tag: null,
    green_tag: null,
    modality: null,
    panel_sex: null
};

export interface GlobalPanel {
	auth_category_id : number,
	auth_users_id: number,
	category_id: number,
	auth_user_email: string,
}

// One row per (panel, trait, gene); the five counts PARTITION the SNPs the wheel draws for that gene,
// so they sum to its catalogue rows. rank is the MIN across them.
export interface PanelRiskRow {
	panel: string;
	trait: string;
	gene: string;
	rank: number | null;
	high_risk: number;
	med_risk: number;
	no_risk: number;
	// A call exists but nothing can be compared — uncallable alleles or no risk allele. The wheel's grey.
	// Absent on payloads from a labiq that predates the partition fix; both read as 0 there.
	no_call?: number;
	// Never measured for this sample. The wheel's ungenotyped blue, an absent result rather than a good one.
	not_genotyped?: number;
}

export interface PanelRiskSummary {
	sample_id: string;
	rows: PanelRiskRow[];
}
